import './Pagination.css';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../../i18n/i18n';
import { ApiResource, useApi } from '../../state/api';
import {
	ApiCategoryMetaData,
	Loop54CategoryProductsRequestBody,
	Loop54SearchRequestBody,
	PaginatedLoop54,
} from '../../types';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { Options } from '../Form/Options';
import { Loader } from '../Loader/Loader';
import { toQuery, useQuery } from '../Routes';
import { Helmet } from 'react-helmet-async';
import { ReactNode } from 'react';
import { useConfig } from '../../config';

type PaginationLoop54Props = {
	path: string;
	params: Record<string, string | number>;
	total: number;
	offset: number;
	limit: number;
	maxPages?: number;
};
export const PaginationLoop54 = ({
	path,
	params,
	total,
	offset,
	limit,
	maxPages = 10,
}: PaginationLoop54Props): JSX.Element => {
	const num = Math.ceil(total / limit);
	const page = 1 + Math.ceil(offset / limit);
	let pages: number[] = [];
	for (let i = 1; i <= num; i++) {
		pages.push(i);
	}

	if (pages.length > maxPages) {
		pages = pages.slice(0, maxPages);
	}

	if (total <= limit) return <></>;

	return (
		<div className="pagination">
			{pages.map((i) => (
				<Link
					className={i === page ? 'active' : ''}
					to={`${path}?${toQuery({
						...params,
						offset: limit * (i - 1),
						limit,
					})}`}
					key={i}
				>
					{i}
				</Link>
			))}
		</div>
	);
};

type AutoPaginationProps = {
	data: { total: number; limit: number; offset: number };
};
export const AutoPagination = ({ data: { total, limit, offset } }: AutoPaginationProps): JSX.Element => {
	const path = useLocation().pathname;
	const params = useQuery<{ offset?: string; limit?: string }>();
	const maxPages = 10;
	delete params.offset;
	delete params.limit;
	return (
		<PaginationLoop54 path={path} params={params} total={total} limit={limit} offset={offset} maxPages={maxPages} />
	);
};

export const LOOP54_DEFAULT_PAGE_SIZE = 24;

const getLoop54PageSize = (isCategoryPage?: boolean, isFirstPage?: boolean) =>
	isCategoryPage && isFirstPage ? 26 : LOOP54_DEFAULT_PAGE_SIZE;

export const FilterChooserCombined = () => {
	const t = useTranslation().filtering;
	return (
		<Options placeholder="">
			<option value="">{t.filterSingular}</option>
		</Options>
	);
};

type Paged = { limit: number; offset: number };

type PagesProps<TRes, TParams extends Paged> = {
	className?: string;
	onRender?: (data: TRes) => void;
	resource: ApiResource<TRes, TParams>;
	params: Unpaginated<TParams>;
	preamble?: ReactNode;
	buildChildren: (data: TRes, isFirstPage: boolean) => JSX.Element;
	descriptionData?: ApiCategoryMetaData;
	postPayload?: Loop54SearchRequestBody | Loop54CategoryProductsRequestBody;
	postHeaders?: Record<string, string>;
	isCategoryPage?: boolean;
	hasDescription?: boolean;
	isDescriptionExpanded?: boolean;
	extraMarkup?: ReactNode[];
};
export function PagesLoop54<TRes extends PaginatedLoop54, TParams extends Paged>({
	className = '',
	resource,
	params,
	preamble,
	extraMarkup,
	onRender,
	buildChildren,
	postPayload,
	postHeaders,
	isCategoryPage,
	hasDescription,
	isDescriptionExpanded,
}: PagesProps<TRes, TParams>) {
	const t = useTranslation().generic;
	const path = useLocation().pathname;
	const { loop54Api } = useConfig();
	const query = useQuery<{ page?: string }>();
	const isFirstPage = query.page === '1' || query.page === undefined;
	const pageSize = getLoop54PageSize(isCategoryPage, isFirstPage);

	const pageCount = +(query.page ?? '1');
	const { data } = useApi(
		resource,
		{
			...params,
			limit: pageSize,
			offset: 0,
		},
		loop54Api,
		{
			...postPayload,
			resultsOptions: {
				...postPayload?.resultsOptions,
				take: pageSize,
				skip: 0,
			},
		},
		postHeaders,
		'POST'
	);
	let isLastPage = data && data.results.count <= pageSize * pageCount;
	if (isLastPage === undefined) isLastPage = false;

	const paginationText = (
		pageCount: number,
		totalResults: number,
		isCategoryPage: boolean | undefined,
		hasDescription?: boolean,
		isDescriptionExpanded?: boolean
	) => {
		if (isCategoryPage) {
			const shownProducts = hasDescription && !isDescriptionExpanded ? 26 + (pageCount - 1) * 24 : pageCount * 24;
			return `${shownProducts} av ${totalResults}`;
		}
		return `${pageCount * pageSize} av ${totalResults}`;
	};

	const showLoadMore = data && data.results.count > pageSize * pageCount;

	let pages: JSX.Element[] = [];
	for (var i = 0; i < pageCount; i++)
		pages.push(
			<Page
				key={i}
				resource={resource}
				params={params}
				index={i}
				buildChildren={buildChildren}
				onRender={onRender}
				postPayload={postPayload}
				postHeaders={postHeaders}
				isCategoryPage={isCategoryPage}
			/>
		);

	const shouldApplyHasDescriptionClass = hasDescription && !isDescriptionExpanded;

	return (
		<>
			{pageCount > 1 && (
				<Helmet>
					<link
						rel="prev"
						href={path + (pageCount > 2 ? '?' + toQuery({ ...query, page: pageCount - 1 }) : '')}
					/>
					{showLoadMore && <link rel="next" href={path + '?' + toQuery({ ...query, page: pageCount + 1 })} />}
				</Helmet>
			)}

			<div
				className={`product-listing ${isCategoryPage && isLastPage ? 'is-last-page' : ''} ${shouldApplyHasDescriptionClass ? 'has-description' : ''} ${className}`}
			>
				{preamble}
				{pages}
				{extraMarkup}
			</div>

			{showLoadMore && (
				<div className="load-more">
					<Link replace to={path + '?' + toQuery({ ...query, page: pageCount + 1 })}>
						{t.loadMore}
					</Link>
					<div className="progressbar">
						<div className="progress-blend" />
						<div
							className="progress"
							style={{ width: `${(100 * (pageCount * pageSize)) / data.results.count}%` }}
						/>
					</div>
					<div>
						{paginationText(
							pageCount,
							data.results.count,
							isCategoryPage,
							hasDescription,
							isDescriptionExpanded
						)}
					</div>
				</div>
			)}
		</>
	);
}

type Unpaginated<T extends Paged> = Omit<Omit<T, 'limit'>, 'offset'>;

type PageProps<TRes, TParams extends Paged> = {
	resource: ApiResource<TRes, TParams>;
	params: Unpaginated<TParams>;
	index: number;
	onRender?: (data: TRes) => void;
	buildChildren: (data: TRes, isFirstPage: boolean) => JSX.Element;
	postPayload?: Loop54SearchRequestBody | Loop54CategoryProductsRequestBody;
	postHeaders?: Record<string, string>;
	isCategoryPage?: boolean;
};
export function Page<TRes, TParams extends Paged>({
	resource,
	params,
	index,
	onRender,
	buildChildren,
	postPayload,
	postHeaders,
	isCategoryPage,
}: PageProps<TRes, TParams>): JSX.Element {
	const { loop54Api } = useConfig();
	const isFirstPage = index === 0;
	const pageSize = getLoop54PageSize(isCategoryPage, isFirstPage);

	let offsetPadding = 0;
	if (isCategoryPage) offsetPadding = isFirstPage ? 0 : 2;

	const { data, error } = useApi(
		resource,
		{
			...params,
			limit: pageSize,
			offset: index * pageSize + offsetPadding,
		} as TParams,
		loop54Api,
		{
			...postPayload,
			resultsOptions: {
				...postPayload?.resultsOptions,
				take: pageSize,
				skip: index * pageSize + offsetPadding,
			},
		},
		postHeaders,
		'POST'
	);
	if (error) return <ErrorMessage key="error" />;
	if (data) {
		onRender?.(data);
		return buildChildren(data, isFirstPage);
	} else
		return (
			<>
				<Loader size="box" key="a" />
				<Loader size="box" key="b" />
				<Loader size="box" key="c" />
				<Loader size="box" key="d" />
				<Loader size="box" key="e" />
			</>
		);
}
